import React, {useEffect, useState} from 'react';
import {Card, Button, Space, Typography, DatePicker, Row, Col, Input, Tag} from 'antd';
import {HistoryOutlined, SearchOutlined, DownloadOutlined} from '@ant-design/icons';
import {internalApi} from '../../common/axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const {Text, Title} = Typography;

const RestorePass = () => {
    const [begin, setBegin] = useState(dayjs().startOf('day').hour(9));
    const [end, setEnd] = useState(dayjs());
    const [loading, setLoading] = useState(false);
    const [lastBegin, setLastBegin] = useState(null);
    const [lastEnd, setLastEnd] = useState(null);

    const [pid, setPid] = useState('');
    const [pidLoading, setPidLoading] = useState(false);
    const [pidResult, setPidResult] = useState(null);
    const [restoreCount, setRestoreCount] = useState(0);
    const [csvLoading, setCsvLoading] = useState(false);
    const [csvPids, setCsvPids] = useState('');

    const fetchLastTime = async () => {
        try {
            const res = await internalApi.get('/player/passRestore/lastTime');
            const {begin: b, end: e} = res.data;
            setLastBegin(b ? dayjs.unix(Number(b)) : null);
            setLastEnd(e ? dayjs.unix(Number(e)) : null);
        } catch (e) {
            // ignore
        }
    };

    useEffect(() => {
        fetchLastTime();
    }, []);

    const handleRestore = async () => {
        if (!begin || !end) {
            toast.error('시작 시간과 마지막 시간을 모두 설정해주세요.');
            return;
        }

        if (begin.isAfter(end)) {
            toast.error('시작 시간이 마지막 시간보다 늦을 수 없습니다.');
            return;
        }

        setLoading(true);
        try {
            const res = await internalApi.post('/player/passRestore/all', {
                begin: begin.unix(),
                end: end.unix(),
            });
            setRestoreCount(res.data.count);
            toast.success(res.data.message || '패스 복구가 완료되었습니다.');
            await fetchLastTime();
        } catch (e) {
            toast.error(e.response?.data?.message || '패스 복구에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCsv = async () => {
        const pids = csvPids.split(',').map(p => p.trim()).filter(Boolean);
        if (pids.length === 0) {
            toast.error('PID를 입력해주세요.');
            return;
        }
        setCsvLoading(true);
        try {
            const res = await internalApi.post('/player/restoreLogs/csv', {pids}, {responseType: 'blob'});
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'goods_and_badge_logs.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('CSV 다운로드에 실패했습니다.');
        } finally {
            setCsvLoading(false);
        }
    };

    const handleSearchPid = async () => {
        if (!pid.trim()) {
            toast.error('PID를 입력해주세요.');
            return;
        }

        setPidLoading(true);
        setPidResult(null);
        try {
            const res = await internalApi.get('/player/passRestore/result', {
                params: {pid: pid.trim()}
            });
            setPidResult(res.data);
        } catch (e) {
            toast.error(e.response?.data?.message || '조회에 실패했습니다.');
        } finally {
            setPidLoading(false);
        }
    };

    return (
        <Space direction="vertical" style={{width: '100%', maxWidth: 600, margin: '24px auto', display: 'flex'}}>
            <Card
                title={
                    <Space align="center" size="middle">
                        <HistoryOutlined style={{fontSize: 20}}/>
                        <div>
                            <Title level={4} style={{margin: 0}}>패스 복구</Title>
                            <Text type="secondary" style={{fontSize: '12px'}}>
                                기간을 설정하여 패스를 일괄 복구합니다.
                            </Text>
                        </div>
                    </Space>
                }
            >
                <Space direction="vertical" style={{width: '100%'}} size="large">
                    {(lastBegin || lastEnd) && (
                        <div style={{
                            padding: '12px 16px',
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 6
                        }}>
                            <Text strong style={{marginBottom: 4, display: 'block'}}>마지막 복구 기록</Text>
                            <Text>
                                {lastBegin?.format('YYYY-MM-DD HH:mm:ss')} ~ {lastEnd?.format('YYYY-MM-DD HH:mm:ss')}
                            </Text>
                        </div>
                    )}
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <Text strong>시작 시간</Text>
                            <DatePicker
                                showTime
                                style={{width: '100%', marginTop: 8}}
                                placeholder="시작 시간 선택"
                                value={begin}
                                onChange={setBegin}
                            />
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text strong>마지막 시간</Text>
                            <DatePicker
                                showTime
                                style={{width: '100%', marginTop: 8}}
                                placeholder="마지막 시간 선택"
                                value={end}
                                onChange={setEnd}
                            />
                        </Col>
                    </Row>
                    <Button
                        type="primary"
                        icon={<HistoryOutlined/>}
                        onClick={handleRestore}
                        loading={loading}
                        disabled={!begin || !end}
                        block
                    >
                        패스 복구
                    </Button>
                    <Text type="success">
                        {restoreCount > 0 && `총 ${restoreCount}개의 패스가 복구되었습니다.`}
                    </Text>
                </Space>
            </Card>

            <Card
                title={
                    <Space align="center" size="middle">
                        <SearchOutlined style={{fontSize: 20}}/>
                        <div>
                            <Title level={4} style={{margin: 0}}>복구 내역 조회</Title>
                            <Text type="secondary" style={{fontSize: '12px'}}>
                                PID로 복구 여부를 확인합니다.
                            </Text>
                        </div>
                    </Space>
                }
            >
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Input.Search
                        placeholder="PID 입력"
                        value={pid}
                        onChange={(e) => {
                            setPid(e.target.value);
                            setPidResult(null);
                        }}
                        onSearch={handleSearchPid}
                        enterButton="조회"
                        loading={pidLoading}
                    />
                    {pidResult && (
                        <div style={{
                            padding: '12px 16px',
                            background: pidResult.result === 1 ? '#f6ffed' : '#fff2f0',
                            border: `1px solid ${pidResult.result === 1 ? '#b7eb8f' : '#ffccc7'}`,
                            borderRadius: 6
                        }}>
                            <Row gutter={[8, 8]}>
                                <Col span={12}>
                                    <Text type="secondary">PID</Text>
                                    <div><Text strong>{pidResult.pid}</Text></div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary">상태</Text>
                                    <div>
                                        {pidResult.result === 1
                                            ? <Tag color="success">복구 완료</Tag>
                                            : <Tag color="error">복구 안함</Tag>
                                        }
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary">복구 시간</Text>
                                    <div>
                                        {pidResult.result === 1
                                            ? <Tag color="success">{pidResult.created}</Tag>
                                            : <Tag color="error">복구 안함</Tag>
                                        }
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary">복구 전 최종 저장 시간</Text>
                                    <div>
                                        {pidResult.result === 1 && pidResult.lastSyncId !== null
                                            ? <Tag color="success">{pidResult.lastSyncId}</Tag> :
                                            pidResult.result === 1 && pidResult.lastSyncId === null ?
                                                <Tag color="error">저장 안됨</Tag>
                                                : <Tag color="default">복구 안함</Tag>
                                        }
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Space>
            </Card>

            <Card
                title={
                    <Space align="center" size="middle">
                        <DownloadOutlined style={{fontSize: 20}}/>
                        <div>
                            <Title level={4} style={{margin: 0}}>복구 로그 다운로드</Title>
                            <Text type="secondary" style={{fontSize: '12px'}}>
                                복구 대상 PID의 재화/뱃지 로그를 CSV로 다운로드합니다.
                            </Text>
                        </div>
                    </Space>
                }
            >
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Input.TextArea
                        placeholder="PID를 쉼표로 구분하여 입력 (예: ABC123, DEF456, GHI789)"
                        value={csvPids}
                        onChange={(e) => setCsvPids(e.target.value)}
                        rows={4}
                    />
                    <Button
                        type="primary"
                        icon={<DownloadOutlined/>}
                        onClick={handleDownloadCsv}
                        loading={csvLoading}
                        disabled={!csvPids.trim()}
                        block
                    >
                        CSV 다운로드
                    </Button>
                </Space>
            </Card>
        </Space>
    );
};

export default RestorePass;